import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import { Event as EventIcon, AccessTime as TimeIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import { eventService, StoreEvent } from '../../services/eventService';
import SEO from '../../components/SEO';

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<StoreEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await eventService.getUpcomingEvents('event');
        setEvents(data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <SEO title="Upcoming Events" description="Check out our upcoming store events and casual play sessions." />
      
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <EventIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" fontWeight="bold">
          Upcoming Events
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : events.length === 0 ? (
        <Box 
          sx={{ 
            py: 10, 
            textAlign: 'center', 
            borderRadius: 4, 
            background: 'rgba(255,255,255,0.01)', 
            border: '1px dashed rgba(255,255,255,0.05)' 
          }}
        >
          <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No upcoming events
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later for new casual play sessions and gatherings.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {events.map((event) => (
            <Grid item xs={12} md={6} key={event.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" fontWeight="bold">
                      {event.title}
                    </Typography>
                    {event.entryFee && (
                      <Chip label={`Entry: ${event.entryFee}`} color="primary" variant="outlined" size="small" />
                    )}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {event.description}
                  </Typography>

                  <Box display="flex" flexDirection="column" gap={1} mt={2}>
                    <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                      <EventIcon fontSize="small" />
                      <Typography variant="body2">
                        {new Date(event.date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                      <TimeIcon fontSize="small" />
                      <Typography variant="body2">{event.time}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} color="text.secondary">
                      <LocationIcon fontSize="small" />
                      <Typography variant="body2">{event.location}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default EventsPage;
